import { useEffect, useState } from 'react';
import {
  outsourceV2IpResourceOptions,
  v2IpResourceOptions,
  type OptionItemVo,
} from '#services/api';

/** v2IpResourceOptions 接口返回的选项集合（IP 合作需求多场景复用，避免各文件重复 fetch） */
export interface IpResourceOptions {
  /** IP 类型选项（供 ip_type 只读 value→name 映射） */
  ipTypes: OptionItemVo[];
  /** 发行区域选项（Apollo 动态下发，供联动所属区域 code→中文名映射） */
  countryRegionOptions: OptionItemVo[];
  /** 产品上架平台选项（Apollo 动态下发，供联动上架平台 code→中文名映射） */
  launchPlatformOptions: OptionItemVo[];
  /** 联动难度选项（Apollo 动态下发，供二级单 ip_difficulty 只读 value→name 映射） */
  cooperationDifficultyOptions: OptionItemVo[];
}

const EMPTY_OPTIONS: IpResourceOptions = {
  ipTypes: [],
  countryRegionOptions: [],
  launchPlatformOptions: [],
  cooperationDifficultyOptions: [],
};

/**
 * 拉取 IP 资源表单选项并拆分三类选项。
 * 封装 NewIPCooperationItem / PrimaryDetailContent / SecondaryDetailContent 三处重复 fetch 逻辑；
 * 带 cancelled 竞态守卫（镜像 v2IpResourceReinvestStatus 轮询范式），组件卸载后不触发 setState。
 *
 * @param scope 数据源作用域：'inner' 走内包端 /api/v2/ip-resource/options（默认），
 *              'external' 走外包端 /api/outsource/v2/ip-resource/options（仅服务外包端，
 *              外包详情联动所属区域等枚举加载用，与内包端同源组装）。
 */
export const useIpResourceOptions = (
  scope: 'inner' | 'external' = 'inner'
): IpResourceOptions => {
  const [options, setOptions] = useState<IpResourceOptions>(EMPTY_OPTIONS);
  useEffect(() => {
    let cancelled = false;
    const api = scope === 'external' ? outsourceV2IpResourceOptions : v2IpResourceOptions;
    api().then((res) => {
      if (cancelled) return;
      if (res?.code === 1) {
        setOptions({
          ipTypes: res?.data?.ip_types || [],
          countryRegionOptions: res?.data?.country_region_options || [],
          launchPlatformOptions: res?.data?.launch_platform_options || [],
          cooperationDifficultyOptions:
            res?.data?.cooperation_difficulty_options || [],
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scope]);
  return options;
};
